/**
 * Live sliders over config — Phase 8.
 *
 * The whole of this phase is one sentence in SPEC1.md ("sliders, all live"), and
 * it is the last phase for a reason: it is the point where tuning stops being
 * something that happens between sessions and becomes something that happens
 * *during* one. Four of Phase 6's shipped thresholds contradict the spec, and
 * every one of them cost a recording, an evening of curve-fitting and a rebuild
 * to find. The thing this file is worth is that the next one costs a drag.
 *
 * ### Reading config at use time is not a style preference
 *
 * `config.js` has said since Phase 0 that nothing may destructure a threshold
 * into module scope. This is the phase that cashes that promise, and the phase
 * where breaking it stops being theoretical: a captured constant produces a
 * control that moves smoothly, updates its readout, records its change, and does
 * nothing. That failure is indistinguishable from a threshold that turns out not
 * to matter, which is the most expensive way to be wrong here. Two places were
 * caught doing it and fixed alongside this file — the envelope's band edges, and
 * the timeline demo's detector latencies.
 *
 * ### Changes are logged into the recording
 *
 * Moving a slider mid-take is the normal way to use this, so a session records
 * both the tuning it started from and every change made while it ran. Without
 * that, a take whose second half was made under different thresholds is a take
 * that reads as a detector behaving inconsistently.
 */

import { config, defaults } from '../core/config.js';
import {
  coerce,
  decimalsOf,
  FIELDS,
  getPath,
  resetTuning,
  setPath,
  TUNABLES,
  tuning,
} from '../core/tunables.js';

export class Controls {
  /**
   * @param {HTMLElement} root
   * @param {object} [o]
   * @param {(field: object, value: *) => void} [o.onChange] one field moved
   * @param {(paths: string[]) => void} [o.onBulkChange] several at once — reset,
   *   or a tuning applied from a URL. Separate because the caller's response is
   *   different: one change is worth logging, forty is worth re-reading.
   */
  constructor(root, { onChange, onBulkChange } = {}) {
    this.root = root;
    this.onChange = onChange ?? (() => {});
    this.onBulkChange = onBulkChange ?? (() => {});
    /** @type {Map<string, object>} */
    this.rows = new Map();

    const frag = document.createDocumentFragment();
    for (const group of TUNABLES) frag.append(this._buildGroup(group));
    root.replaceChildren(frag);
    this.refresh();
  }

  _buildGroup(group) {
    const details = document.createElement('details');
    details.className = 'tune-group';
    details.open = Boolean(group.open);

    const summary = document.createElement('summary');
    const title = document.createElement('span');
    title.className = 'tune-title';
    title.textContent = group.title;
    // Shows how many controls in this group are away from their shipped value,
    // so a collapsed group cannot hide the change that is confusing you.
    const badge = document.createElement('span');
    badge.className = 'tune-badge';
    badge.hidden = true;
    summary.append(title, badge);
    details.append(summary);

    if (group.note) {
      const note = document.createElement('p');
      note.className = 'note';
      note.textContent = group.note;
      details.append(note);
    }

    const list = document.createElement('div');
    list.className = 'tune-fields';
    for (const field of group.fields) list.append(this._buildField(field));
    details.append(list);

    this._badges ??= [];
    this._badges.push({ badge, group });
    return details;
  }

  _buildField(field) {
    const row = document.createElement('div');
    row.className = 'tune-row';

    const label = document.createElement('label');
    label.className = 'tune-label';
    label.textContent = field.label;
    label.htmlFor = `tune-${field.path}`;
    // The dotted path is the thing you paste at me. Kept out of the visible
    // label because forty of them is unreadable, kept in the tooltip because
    // "plosive attack" is not enough to find in a file.
    label.title = field.path;

    // A <select> is a different element, not a different input type — so the
    // control is built first and everything after it works on whatever came back.
    const input =
      field.type === 'select' ? document.createElement('select') : document.createElement('input');
    input.id = `tune-${field.path}`;
    if (field.type === 'select') {
      input.className = 'tune-select';
      for (const option of field.options) {
        const el = document.createElement('option');
        el.value = option.value;
        el.textContent = option.label;
        input.append(el);
      }
    } else if (field.type === 'boolean') {
      input.type = 'checkbox';
      input.className = 'tune-check';
    } else {
      input.type = 'range';
      input.min = String(field.min);
      input.max = String(field.max);
      input.step = String(field.step);
      input.className = 'tune-range';
    }

    const value = document.createElement('output');
    value.className = 'tune-value mono';

    // Only appears once the control has been moved. A reset button on every row
    // at all times is forty pieces of furniture describing a state that is not
    // the case.
    const reset = document.createElement('button');
    reset.type = 'button';
    reset.className = 'tune-reset';
    reset.textContent = '↺';
    reset.title = `back to ${format(field, getPath(defaults, field.path))}`;
    reset.hidden = true;
    reset.addEventListener('click', () => {
      this.set(field.path, getPath(defaults, field.path));
    });

    const hint = document.createElement('span');
    hint.className = 'tune-hint';
    hint.textContent = field.hint ?? '';

    input.addEventListener('input', () => {
      let raw;
      if (field.type === 'boolean') raw = input.checked;
      else if (field.type === 'select') raw = input.value;
      else raw = Number(input.value);
      this.set(field.path, raw);
    });

    row.append(label, input, value, reset, hint);
    this.rows.set(field.path, { field, input, value, reset, row });
    return row;
  }

  /**
   * Move one control, from anywhere — the slider itself, its reset button, or a
   * tuning decoded out of the URL.
   *
   * @returns {boolean} whether the write landed
   */
  set(path, raw) {
    const row = this.rows.get(path);
    if (!row) return false;
    const value = coerce(row.field, raw);
    if (value === null || !setPath(config, path, value)) return false;
    this._paint(row);
    this._paintBadges();
    this.onChange(row.field, value);
    return true;
  }

  /** Put everything back where it shipped. */
  resetAll() {
    resetTuning();
    this.refresh();
    this.onBulkChange(FIELDS.map((f) => f.path));
  }

  /**
   * Pull every control back from config.
   *
   * Needed because config is not owned here. LAG has two other sliders on this
   * page — one beside the trace where it is calibrated, one beside the timeline
   * where its effect on ordering is visible — and all three write the same
   * field. A control rack that only wrote would sit there showing 320 while the
   * value was 480.
   */
  refresh() {
    for (const row of this.rows.values()) this._paint(row);
    this._paintBadges();
  }

  _paint(row) {
    const { field, input, value, reset } = row;
    const current = getPath(config, field.path);
    const shipped = getPath(defaults, field.path);

    if (field.type === 'boolean') input.checked = Boolean(current);
    else input.value = String(current);
    // A select shows its own label, so the value column would repeat it.
    if (field.type === 'select') value.hidden = true;

    value.textContent = format(field, current);
    const moved = current !== shipped;
    reset.hidden = !moved;
    row.row.classList.toggle('moved', moved);
  }

  _paintBadges() {
    const changed = tuning();
    for (const { badge, group } of this._badges ?? []) {
      const n = group.fields.filter((f) => f.path in changed).length;
      badge.hidden = n === 0;
      badge.textContent = `${n} moved`;
    }
  }
}

/** A value as it reads on the control: step precision, plus the unit. */
function format(field, value) {
  if (field.type === 'boolean') return value ? 'on' : 'off';
  if (field.type === 'select') {
    return field.options.find((o) => o.value === value)?.label ?? String(value);
  }
  const text = Number(value).toFixed(decimalsOf(field.step));
  return field.unit ? `${text} ${field.unit}` : text;
}

export default Controls;
